import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { loadInitialNotifications } from '../../store/redux/actions/notificationActions';
import { revalidateUserConfig } from '../../store/redux/actions/userActionCreator';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import Footer from '../footer';
import { useFilteredNavigationState } from '../../hooks/useFilteredNavigation';
import { useCompanyRulesSignalR } from '../../hooks/useCompanyRulesSignalR';
import PendingTermsGate from '../terms/PendingTermsGate';
import SubscriptionBanner from '../subscription/SubscriptionBanner';
import SandboxDemoBanner from '../sandbox/SandboxDemoBanner';
import RestrictedBanner from '../subscription/RestrictedBanner';
import LockedShell from '../subscription/LockedShell';
import PageConfigGuard from '../PageConfigGuard';
import { useMySubscription } from '../../hooks/useMySubscription';
import { getLockedModule } from '../../data/lockedRoutes';
import { getBrochureSlugFromPath, isConnectHiddenModule } from '../../data/brochureModules';
import { useTreatAsFree } from '../../hooks/useTreatAsFree';
import { Skeleton } from '../ui/Skeleton';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import BootErrorPanel from '../BootErrorPanel';
import NotificationModulePrompt from '../notifications/NotificationModulePrompt';
import { track, AnalyticsEvents } from '../../utils/analytics';

/**
 * Master layout component combining Sidebar, TopBar, Footer with content outlet.
 *
 * Layout Structure (viewport-height based):
 * - Sidebar on the left spanning full height (header aligns with TopBar)
 * - Right section with:
 *   - TopBar at top
 *   - Main content with independent scrolling
 *   - Footer at bottom
 *
 * Provides:
 * - Responsive sidebar (desktop: collapsible, mobile: overlay)
 * - Top navigation bar with search, notifications, user menu
 * - Content area with React Router Outlet
 * - Role-based navigation filtering via useFilteredNavigationState hook, passed down to the routed
 *   page as outlet context so a route can answer "what can this user actually see" without
 *   re-fetching the company's module and page config
 * - No horizontal scroll on any standard viewport
 */
const MasterLayout: React.FC = () => {
  const { t } = useTranslation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Role-filtered navigation modules. The loaded flag rides along because HomeRoute has to know
  // the difference between "this company has no Home module" and "the config has not arrived yet".
  const navigation = useFilteredNavigationState();
  const filteredModules = navigation.modules;

  // Subscribe to real-time company rules updates via SignalR
  useCompanyRulesSignalR();

  const dispatch = useDispatch();
  const userId = useSelector((state: any) => state.user.userId);
  const role = useSelector((state: any) => state.user.role);
  const isLoggedIn = useSelector((state: any) => state.user.isLoggedIn);
  const firstLoad = useSelector((state: any) => state.user.firstLoad);

  // Backstop for the "shell with an empty user" class. PrivateRoute now refuses
  // to mount this layout until INIT has landed, so in principle we never see it
  // — which is exactly why it is worth one event if we ever do. That state used
  // to render a reduced nav, a 'U' avatar and a permanent skeleton while
  // emitting NOTHING: no request, no 4xx, no console error. Never again.
  const bootStateInvalid = !!isLoggedIn && (!userId || !role);
  const reportedBootState = useRef(false);
  useEffect(() => {
    if (bootStateInvalid && !reportedBootState.current) {
      reportedBootState.current = true;
      track(AnalyticsEvents.BootStateInvalid, {
        role: role ?? '',
        hasUserId: !!userId,
        firstLoad: !!firstLoad,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      });
    }
  }, [bootStateInvalid, role, userId, firstLoad]);

  // Load initial notification count for bell badge
  useEffect(() => {
    if (userId) {
      (dispatch as any)(loadInitialNotifications(userId));
    }
  }, [userId, dispatch]);

  // Reconcile the persisted Redux snapshot with the server once per app load.
  // The whole user slice is cached in localStorage (redux-persist) and the
  // firstLoad-gated refresh in PrivateRoute never re-runs for a returning
  // session, so config like the attendance-image / face-recognition rules
  // (which decide whether the clocking dialog opens the camera) can be stale
  // after an admin changes it while this session was closed. This re-fetches
  // getUserInfo once and updates Redux only for the fields that changed.
  //
  // PrivateRoute's boot fetch hits the SAME endpoint on the same cold load. The
  // two are de-duplicated by the in-flight coalescer in api/General/account.ts,
  // so overlapping calls share one request — do NOT "fix" the duplication by
  // coupling this effect to PrivateRoute's.
  const didRevalidate = useRef(false);
  useEffect(() => {
    if (didRevalidate.current || !userId) return;
    didRevalidate.current = true;
    (dispatch as any)(revalidateUserConfig());
  }, [userId, dispatch]);

  // ---------- Phase 5: route-level Free-tier lock ----------
  // Resolves the locked-module slug for the current pathname when the caller
  // is Free-tier AND not an Operator. We also honour the Operator preview
  // hook `?previewTier=free` so operators can dogfood the soft-lock without
  // mutating company state — but only when the live session is actually
  // an Operator (mirrors SubscriptionBanner's preview guard so a regular
  // user can't smuggle in the lock via URL).
  const location = useLocation();
  const { data: subscriptionData, isError: subscriptionError, refetch: refetchSubscription } =
    useMySubscription();
  const [planCheckBandDismissed, setPlanCheckBandDismissed] = useState(false);
  // The /api/Subscription/me payload is what tells us the tier. Before it
  // resolves, useMySubscription falls back to "Free" (coerceTier(undefined)
  // returns "Free"), which would briefly wrap Paid/Trial users in LockedShell
  // and flash the locked-preview chrome at them on every page load. Gate the
  // soft-lock decision on the first response (success OR error). On error we
  // fail open — better to give the user their full app than block them
  // indefinitely if /me momentarily 5xxs.
  //
  // 🔴 TWO DISTINCT FLAGS, AND THE DIFFERENCE IS A PAYING CUSTOMER'S APP.
  // `subscriptionResolved` means "/me actually answered". `subscriptionReady`
  // means "stop showing the skeleton" and is deliberately also true on error,
  // because a tenant must never be stuck behind a loading placeholder.
  //
  // The lock decision MUST use `subscriptionResolved`. Using the ready flag
  // meant that on error `data` stayed null → coerceTier(undefined) → "Free" →
  // isFree true, AND the error itself made the flag true — so an errored /me
  // wrapped a PAID tenant in the Free-tier LockedShell brochure. That is the
  // exact opposite of the fail-open intent stated above, and it shipped.
  const subscriptionResolved = subscriptionData !== null;
  const subscriptionReady = subscriptionResolved || subscriptionError;
  // Only ever lock on a RESOLVED payload. Operator carve-out and the
  // `?previewTier=free` operator preview path are unchanged — the predicate now
  // lives in useTreatAsFree so the navigation filter shares it verbatim.
  const treatAsFree = useTreatAsFree();
  const lockedModule = treatAsFree ? getLockedModule(location.pathname) : null;
  // Owner request 2026-10-05: Mowazaf Connect never SHOWCASES Recruitment or Tournaments
  // (CONNECT_HIDDEN_MODULE_SLUGS in data/brochureModules.ts). Their routes would otherwise render the
  // LockedShell brochure, and /brochure/recruitment|tournament the full sales page. Instead they get
  // exactly what a hidden page gets: PageConfigGuard's "turned off" notice, then its CO-500-safe
  // redirect. Only reachable when treatAsFree, so Paid/Trial/HRMS tenants and Operators are unaffected.
  const connectHiddenRoute =
    treatAsFree &&
    (isConnectHiddenModule(lockedModule) || isConnectHiddenModule(getBrochureSlugFromPath(location.pathname)));

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  // The drawer is a per-gesture thing: it opens from the hamburger and nothing else. A route
  // change (in-drawer tap, back button, programmatic navigate) closes it, and it does not survive
  // a reload because it is never persisted. `isSidebarCollapsed` is the DESKTOP preference and
  // must stay out of this — see the `variant` prop on Sidebar.
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // Refuse to paint a shell we already know is wrong (see `bootStateInvalid`).
  // Retry is a full reload rather than a local re-fetch: PrivateRoute owns the
  // boot sequence, and the transient boot flags are no longer persisted (see
  // store.ts), so a reload is a genuinely clean re-boot rather than a re-entry
  // into the same broken state.
  if (bootStateInvalid) {
    return <BootErrorPanel onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Pending platform-terms acceptance — mounts a non-dismissible modal when
          the user owes acceptances on the currently published versions. */}
      <PendingTermsGate />

      {/* Desktop Sidebar - full height, next to topbar */}
      <Sidebar
        variant="desktop"
        modules={filteredModules}
        isCollapsed={isSidebarCollapsed}
        onToggle={toggleSidebar}
        /* `navigation.loaded` is true on a SUCCESSFUL fetch and on a FAILED one alike — the same
           shape as `subscriptionReady` above, and for the same reason: a tenant must never be stuck
           behind a loading placeholder. On error the config stays empty, the app fails open to the
           full module list, and the skeleton still clears. */
        isLoadingModules={!navigation.loaded || !subscriptionReady}
      />

      {/* Mobile Sidebar Overlay (only when open). This is the ONLY thing that may open the drawer;
          the desktop instance above never renders the overlay, whatever its collapse preference. */}
      {isMobileSidebarOpen && (
        <Sidebar
          variant="mobile"
          modules={filteredModules}
          isCollapsed={false}
          onToggle={toggleMobileSidebar}
          isLoadingModules={!navigation.loaded || !subscriptionReady}
        />
      )}

      {/* Right Section: TopBar + Content + Footer */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top Navigation Bar. CO-853: page search is handed the SAME filtered modules as the two
            sidebars above, so it can only offer a page this user's sidebar offers. */}
        <TopBar onMobileMenuClick={toggleMobileSidebar} modules={filteredModules} />

        {/* Main Content Area - independent scroll.
            The SubscriptionBanner is mounted as the FIRST child inside <main>
            so it sticks to the top of the scrollable area (i.e. directly under
            the TopBar) without needing to invent a third flex row. The banner
            itself is `position: sticky; top: 0` and removes its own padding so
            the band can stretch edge-to-edge.
            The PendingTermsGate's modal is z-[100]+ — our banner sits at z-30
            and the brochure overlay at z-40, well clear of it.
            `relative` is LOAD-BEARING, do not remove it (CO-872). It makes <main> the containing
            block for every absolutely positioned descendant that has no positioned ancestor of its
            own: Tailwind `sr-only`, the hidden input Radix `Switch` / `RadioGroup` render inside a
            form, a bare `absolute`. Without it those resolve against the viewport, escape this
            element's overflow clip and stretch <html>: a phantom second scrollbar at the window
            edge, or a page wider than the phone. With it they are clipped by, and scroll with,
            <main>. It sets no z-index, so it creates no stacking context and the z-order above is
            unchanged; the sticky banners are unaffected. Consequence for page code: a bare
            `absolute inset-0` inside a page now covers <main>'s first screenful, not the window.
            Anchor an overlay to the element it is meant to cover. An element inside an INNER
            scroller (an `overflow-x-auto` table wrapper, a `max-h-*` list) still needs its own
            `relative` on that scroller: <main> does not clip for it. */}
        <main className="relative flex-1 min-h-0 overflow-x-hidden overflow-y-auto bg-background">
          {/* sandbox.mowazaf.com only: "shared demo, resets nightly" + sign-up / sales CTAs. Null elsewhere. */}
          <SandboxDemoBanner />
          <SubscriptionBanner />
          {/* `pb-28` RESERVES the corner the AI assistant FAB occupies. The launcher is
              `fixed bottom-6 end-6` at 64px (components/ai-chat/AIAssistant.tsx), so it owns an
              88px-tall strip of the VIEWPORT on every authenticated screen — scrolling to the end
              of a page used to leave the last card (390px) or the last row (1440px) underneath it
              with no way to reach it. The FAB's dodge lifts it off CLICKABLE elements; it cannot
              help read-only content, and it should not have to: reserving the space is what makes
              the bottom of every page reachable. A `sticky bottom-0` StickyActionBar is unaffected
              — it sticks to the scrollport edge, not to the content box — and the FAB stays above
              it (z-[1300]) exactly as before. */}
          <div className="p-4 md:p-6 pb-28">
            {/* Payment lapsed → writes are blocked server-side. This band explains
                that, in place, with the amount and the data promise. It is NOT
                LockedShell: that is the Free-tier upsell brochure and this is a
                paying customer whose card failed, which is a different message
                entirely. It renders itself null unless the caller is HR/Admin AND
                the access state is Restricted or Dormant, so an employee never
                learns a billing surface exists — they get the translated
                `subscription_restricted` toast instead. */}
            <RestrictedBanner />
            {/* CO-375 / E13 — HR's inline notification opt-in on the first visit to a module. Renders
                null for every other role, off-module routes and answered modules; never over a
                LockedShell brochure (a Free tenant is not being asked about a module it cannot use). */}
            {subscriptionReady && !lockedModule && <NotificationModulePrompt />}
            {/* /api/Subscription/me failed and we are now failing OPEN — the
                tenant keeps their full app. Say so, because the alternative is
                silently un-gating a genuinely Free tenant with nobody noticing.
                Dismissible: it is an advisory, not a blocker. */}
            {subscriptionError && !subscriptionResolved && !planCheckBandDismissed && (
              <div
                role="status"
                aria-live="polite"
                data-testid="subscription-check-failed-band"
                className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={18} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" />
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        {t("Couldn't verify your plan")}
                      </p>
                      <p className="max-w-[70ch] text-sm text-muted-foreground">
                        {t('Some parts of the app may look limited until we can check your subscription.')}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 self-start">
                    <Button variant="outline" size="sm" onClick={() => { void refetchSubscription(); }} iconName="RefreshCw">
                      {t('Retry')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPlanCheckBandDismissed(true)}>
                      {t('Dismiss')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {/* PageConfigGuard sits OUTSIDE the subscription branches and wraps the outlet once, so
                every authenticated route inherits the Operator's module/page switches — including any
                route added later. Per-route wrapping was rejected: a guard you must remember to add is
                a guard that gets forgotten, and these switches are what withhold demo-only surfaces
                from live tenants. It withholds a surface, never data — see the component's notes.
                CO-500: it is handed the SAME filtered `navigation` the outlet gets, so the page it
                redirects a blocked user to is one that user can actually open. Without this it fell
                back to `/`, which the `home` module owns — a dead end for any tenant with Home off. */}
            <PageConfigGuard navigation={navigation} forceBlocked={connectHiddenRoute}>
              {!subscriptionReady ? (
                <PageContentSkeleton />
              ) : lockedModule ? (
                <LockedShell moduleSlug={lockedModule}>
                  <Outlet context={navigation} />
                </LockedShell>
              ) : (
                <Outlet context={navigation} />
              )}
            </PageConfigGuard>
          </div>
        </main>

        {/* Footer */}
        <Footer>
          <span style={{ fontSize: '10px' }}>
            {t("Copyrights Powered by Mowazaf Technologies All Rights Reserved", { year: new Date().getFullYear() })}
          </span>
        </Footer>
      </div>
    </div>
  );
};

/**
 * Generic page placeholder shown while /api/Subscription/me is resolving.
 * Mirrors the typical PageShell rhythm — header strip, then a few content
 * cards — so the layout shift when the real page mounts is minimal.
 */
const PageContentSkeleton: React.FC = () => (
  <div className="space-y-6" aria-label="Loading">
    {/* Page header row: title + actions */}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-32" />
      </div>
    </div>
    {/* Stat cards row */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-4 space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
    {/* Main content block */}
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <Skeleton className="h-5 w-40" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-10/12" />
        <Skeleton className="h-4 w-9/12" />
      </div>
    </div>
  </div>
);

export default MasterLayout;
